import { FC, useCallback, useState } from 'react';
import { useSetAtom } from 'jotai';
import { Button, Loading } from '@carbon/react';
import { Copy } from '@carbon/react/icons';
import {
  addNotificationsAtom,
  NotificationGroup,
} from 'components/atomised-notifications/atoms';
import { cloneScenarioAtom } from './atoms';

interface CloneButtonProps {
  id: number;
  onCloned: () => void;
}

/**
 * Row-level "Clone scenario" action. Owns its own in-flight state so the
 * containing columns array stays referentially stable across clone clicks
 * (no per-click re-creation). The shared clone atom is fire-and-forget here;
 * we surface progress per row via local state instead of subscribing to the
 * atom's loadable.
 * @param {CloneButtonProps} props - Row id + post-clone callback
 * @return {JSX.Element} Either a Copy icon button or a small spinner
 */
const CloneButton: FC<CloneButtonProps> = ({ id, onCloned }) => {
  const triggerClone = useSetAtom(cloneScenarioAtom);
  const addNotifications = useSetAtom(addNotificationsAtom);
  const [isCloning, setIsCloning] = useState(false);

  const handleClick = useCallback(() => {
    setIsCloning(true);
    triggerClone(id)
      .then(onCloned)
      .catch(() => {
        addNotifications(NotificationGroup.DifferentialScenarios, [
          {
            id: `clone-error-${id}-${Date.now()}`,
            title: 'Clone failed',
            message:
              'Could not clone this differential scenario. Please try again.',
            kind: 'error',
            autoHideMs: 5000,
          },
        ]);
      })
      .finally(() => setIsCloning(false));
  }, [addNotifications, id, onCloned, triggerClone]);

  if (isCloning) {
    return <Loading small withOverlay={false} description="Cloning…" />;
  }
  return (
    <Button
      kind="ghost"
      size="sm"
      hasIconOnly
      renderIcon={Copy}
      iconDescription="Clone scenario"
      tooltipPosition="left"
      onClick={handleClick}
    />
  );
};

export { CloneButton };
